import React, { useState, useRef, useEffect } from 'react';
import { GoogleMap, Marker, useJsApiLoader, DirectionsRenderer } from '@react-google-maps/api';
import { message, Spin } from 'antd';
import breakdownService from '../services/breakdownService';
import companyService from '../services/companyService';
import MapModal from '../components/mapModal';
import { getCurrentLocation } from '../utils/locationUtils';
import { getUserRole } from '../utils/tokenUtils';
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { getUserDetails } from '../utils/cookieUtils';


const Map = () => {
    const googleMapsApiKey = process.env.VITE_GOOGLE_MAPS_API_KEY || 'YOUR_API_KEY';
    
    // Debug: Check if running in development
    console.log('Environment:', process.env.NODE_ENV);
    console.log('Google Maps API Key:', googleMapsApiKey ? 'Present' : 'MISSING');
    
    // Check if running on localhost
    if (typeof window !== 'undefined') {
        console.log('Current hostname:', window.location.hostname);
    }
    const [mapType, setMapType] = useState('roadmap');
    const mapRef = useRef();
    const [breakdowns, setBreakdowns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [markers, setMarkers] = useState([]);
    const [map, setMap] = React.useState(null);
    const [visible, setVisible] = useState(false);
    const [breakdown, setBreakdown] = useState(null);
    const [currentLocation, setCurrentLocation] = useState(null);
    const [messageApi, contextHolder] = message.useMessage();
    const [center, setCenter] = useState({ lat: -28.4793, lng: 24.6727 });
    const [userRole, setUserRole] = useState('');
    console.log('Initializing Google Maps with API key:', googleMapsApiKey ? 'API key is set' : 'API key is MISSING');
    
    const { isLoaded, loadError } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: googleMapsApiKey || 'YOUR_API_KEY',
        preventGoogleFontsLoading: true,
        version: 'weekly',
        libraries: ['places']
    });
    
    console.log('useJsApiLoader state:', { isLoaded, loadError });

    useEffect(() => {
        console.log('Google Maps API status:', { isLoaded, loadError, googleMapsApiKey: googleMapsApiKey ? 'API key set' : 'API key missing' });
        if (loadError) {
            console.error('Error loading Google Maps API:', loadError);
            messageApi.error('Failed to load Google Maps. Please check your internet connection and try again.');
        }
    }, [isLoaded, loadError]);

    const clustererRef = useRef();

    const getUser = () => {
        try {
            const userRoleResponse = getUserRole();
            if (userRoleResponse) {
                setUserRole(userRoleResponse);
            } else {
                setTimeout(() => {
                    const retryRole = getUserRole();
                    if (retryRole) setUserRole(retryRole);
                }, 1000);
            }
        } catch (error) {
            console.error('Failed to get user role:', error);
        }
    }

    useEffect(() => {
        getUser();
    }, []);

    const dynamicUserRoleView = async () => {
        if (userRole === 'MECHANIC') {
            await MechanicView();
        } else if (userRole === 'OWNER') {
            OwnerView();
        } else if (userRole === 'SUPPLIER') {
            SupplierView();
        }
    };

    const mechanicMarker = async () => {
        const MAX_RETRIES = 3;
        const BASE_DELAY = 1000;

        let retryCount = 0;
        let locationSuccess = false;

        while (retryCount < MAX_RETRIES && !locationSuccess) {
            try {
                await getMechanicLocation();
                locationSuccess = true;
            } catch (error) {
                retryCount++;
                console.warn(`Location fetch failed (attempt ${retryCount}/${MAX_RETRIES}):`, error);

                if (retryCount < MAX_RETRIES) {
                    const delay = BASE_DELAY * Math.pow(2, retryCount - 1);
                    console.log(`Retrying in ${delay}ms...`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                } else {
                    console.error('Failed to get location after maximum retries');
                    return;
                }
            }
        }

        const userDetails = getUserDetails();

        let mechanic = null;
        if (currentLocation) {
            mechanic = {
                type: 'mechanic',
                title: 'Me',
                icon: '🔧',
                lat: currentLocation.lat,
                lng: currentLocation.lng,
                mobileNumber: userDetails.mobileNumber,
                firstName: userDetails.fullName,
                companyName: userDetails.companyName,
            };
        }

        if (mechanic !== null) {
            setMarkers(prevMarkers => {
                const hasMe = prevMarkers.some(m => m.title === 'Me');
                if (hasMe) return prevMarkers;
                return [...prevMarkers, mechanic];
            });
        }
    }

    useEffect(() => {
        if (userRole && currentLocation) {
            if (userRole === 'MECHANIC') {
                const hasMechanicMarker = markers.some(marker => marker.title === 'Me');
                if (!hasMechanicMarker) {
                    mechanicMarker();
                }
            }
        }
    }, [userRole, currentLocation]);


    const MechanicView = async () => {

        try {

            await getMechanicLocation();

            const breakdownMarkers = breakdowns.map((breakdown) => {
                const lat = parseFloat(breakdown.latitude);
                const lng = parseFloat(breakdown.longitude);
                if (isNaN(lat) || isNaN(lng)) {
                    console.warn(`Invalid coordinates for breakdown ${breakdown.id}:`, {
                        latitude: breakdown.latitude,
                        longitude: breakdown.longitude
                    });
                    return null;
                }

                return {
                    id: breakdown.id,
                    type: 'breakdown',
                    title: breakdown.breakdownReference,
                    icon: '🚚',
                    lat: lat,
                    lng: lng,
                    ...breakdown,
                };
            }).filter(marker => marker !== null);

            setMarkers(prev => {
                const mechanic = prev.find(m => m.title === 'Me');
                return mechanic ? [mechanic, ...breakdownMarkers] : breakdownMarkers;
            });
        } catch (error) {
            console.error('Failed to get mechanic location:', error);
            messageApi.error('Failed to get mechanic v');
        }

    }

    const OwnerView = async () => {

        try {
            const breakdownMarkers = breakdowns.map((breakdown) => {
                const lat = parseFloat(breakdown.latitude);
                const lng = parseFloat(breakdown.longitude);

                if (isNaN(lat) || isNaN(lng)) {
                    console.warn(`Invalid coordinates for breakdown ${breakdown.id}:`, {
                        latitude: breakdown.latitude,
                        longitude: breakdown.longitude
                    });
                    return null;
                }

                return {
                    id: breakdown.id,
                    type: 'breakdown',
                    title: breakdown.breakdownReference,
                    icon: '🚚',
                    lat: lat,
                    lng: lng,
                    ...breakdown,
                };
            }).filter(marker => marker !== null);

            setMarkers(breakdownMarkers);
        }
        catch (error) {
            console.error('Failed to get owner location:', error);
            messageApi.error('Failed to get owner view');
        }
    }

    const SupplierView = async () => {

        try {
            let mechanicList = [];
            let supplierMarkers = [];
            try {
                const response = await companyService.fetchCompanyMechanics();
                if (response && response.success === true) {
                    mechanicList = response.data;
                }
            } catch (error) {
                console.error('Failed to get current location', error);
                messageApi.error('Failed to get supplier mechanics');
            }

            const mechanicMarkers = mechanicList.map((mechanic) => {

                const lat = parseFloat(mechanic.latitude);
                const lng = parseFloat(mechanic.longitude);

                if (isNaN(lat) || isNaN(lng)) {
                    console.warn(`Invalid coordinates for breakdown ${mechanic.id}:`, {
                        latitude: mechanic.latitude,
                        longitude: mechanic.longitude
                    });
                    return null;
                }

                const firstName = mechanic.fullName.split(' ')[0];

                return {
                    id: mechanic.id,
                    type: 'mechanic',
                    icon: '🔧',
                    lat: lat,
                    lng: lng,
                    mobileNumber: mechanic.mobileNumber,
                    title: firstName,
                    firstName: mechanic.fullName,
                    companyName: mechanic.companyName,
                };
            }).filter(marker => marker !== null);

            const breakdownMarkers = breakdowns.map((breakdown) => {
                const lat = parseFloat(breakdown.latitude);
                const lng = parseFloat(breakdown.longitude);

                if (isNaN(lat) || isNaN(lng)) {
                    console.warn(`Invalid coordinates for breakdown ${breakdown.id}:`, {
                        latitude: breakdown.latitude,
                        longitude: breakdown.longitude
                    });
                    return null;
                }

                return {
                    id: breakdown.id,
                    type: 'breakdown',
                    title: breakdown.breakdownReference,
                    icon: '🚚',
                    lat: lat,
                    lng: lng,
                    ...breakdown,
                };
            }).filter(marker => marker !== null);

            supplierMarkers.push(...mechanicMarkers);
            supplierMarkers.push(...breakdownMarkers);
            setMarkers(supplierMarkers);

        } catch (error) {
            console.error('Failed to get supplier view', error);
            messageApi.error('Failed to get supplier view');
        }
    }

    useEffect(() => {
        if (userRole && breakdowns.length > 0) {
            dynamicUserRoleView();
        }
    }, [breakdowns, userRole]);


    const createCustomIcon = (emoji) => {

        let yOffset = 35;
        if (emoji === '🚚') yOffset = 35;
        if (emoji === '🔧') yOffset = 38;

        return {
            url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
        <svg width="70" height="70" xmlns="http://www.w3.org/2000/svg">
            <circle cx="35" cy="35" r="14" fill="white" stroke="black" stroke-width="1.5" />
            <text x="35" y="${yOffset}" font-size="15" text-anchor="middle" dominant-baseline="middle">${emoji}</text>
        </svg>
    `)}`,
            scaledSize: new window.google.maps.Size(100, 100),
            anchor: new window.google.maps.Point(50, 50),
        };
    };

    const getMechanicLocation = async () => {
        try {
            const position = await getCurrentLocation();
            if (position) {
                setCurrentLocation({ lat: position.latitude, lng: position.longitude });
            }
        } catch (error) {
            console.error('Failed to get current location', error);
        }
    };

    const onLoad = React.useCallback(function callback(map) {
        console.log('Google Map loaded successfully');
        console.log('Map center:', map.getCenter().toJSON());
        console.log('Map zoom:', map.getZoom());
        setMap(map);

        clustererRef.current = new MarkerClusterer({
            map,
            markers: [],
            renderer: {
                render: ({ count, position }) => new google.maps.Marker({
                    position,
                    label: { text: String(count), color: "white", fontSize: "20px" },
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        scale: 20,
                        fillColor: "#4285F4",
                        fillOpacity: 0.6,
                        strokeWeight: 2,
                    },
                }),
            },
        });
    }, [center]);

    const onUnmount = React.useCallback(function callback(map) {
        setMap(null);
    }, []);

    const handleMapTypeChange = (type) => {
        setMapType(type);
        if (map) {
            map.setMapTypeId(type);
        }
    };
    const onCancel = () => {
        setVisible(false);
    };

    const fetchBreakdowns = async () => {
        try {
            setLoading(true);

            const isMechanic = userRole === 'MECHANIC';
            const response = isMechanic
                ? await breakdownService.getAssignedBreakdowns()
                : await breakdownService.getVisibleBreakdowns();

            let apiBreakdowns = [];
            if (Array.isArray(response)) {
                apiBreakdowns = response;
            } else if (Array.isArray(response?.breakdowns)) {
                apiBreakdowns = response.breakdowns;
            } else if (Array.isArray(response?.data)) {
                apiBreakdowns = response.data;
            }

            if (response && response.success === false) {
                messageApi.error('Failed to load breakdowns. Please try again later.');
            }

            setBreakdowns(apiBreakdowns);

        } catch (error) {
            console.error('Error fetching breakdowns:', error);
            messageApi.error('An error occurred while fetching breakdowns');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (userRole) {
            fetchBreakdowns();
        }
    }, [userRole]);

    useEffect(() => {
        if (!map || !clustererRef.current) return;

        clustererRef.current.clearMarkers();

        const googleMarkers = markers.map(marker => {
            const googleMarker = new google.maps.Marker({
                position: { lat: marker.lat, lng: marker.lng },
                icon: createCustomIcon(marker.icon || '📍'),
                map: map,
                title: marker.title,
            });

            googleMarker.originalData = marker;

            googleMarker.addListener('click', () => {
                handleMarkerClick(marker);
            });

            return googleMarker;
        });

        clustererRef.current.addMarkers(googleMarkers);
    }, [markers, map]);

    const handleMarkerClick = (marker) => {
        if (marker.type === 'Me') {
            return;
        }
        setBreakdown(marker);
        setVisible(true);
        setCenter({ lat: marker.lat, lng: marker.lng });
    };

    console.log('Rendering map component. isLoaded:', isLoaded, 'loadError:', loadError);
    
    if (!isLoaded) {
        return (
            <div style={{
                width: '100%',
                height: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '20px',
                textAlign: 'center'
            }}>
                {loadError ? (
                    <>
                        <h2>Error Loading Map</h2>
                        <p style={{ color: 'red', margin: '10px 0' }}>{loadError.message || 'Failed to load Google Maps'}</p>
                        <p>Please check your internet connection and make sure you have a valid Google Maps API key.</p>
                        <p>If you're the developer, check the console for more details.</p>
                    </>
                ) : (
                    <>
                        <Spin size="large" />
                        <p style={{ marginTop: '20px' }}>Loading map...</p>
                        <p style={{ fontSize: '0.9em', color: '#666', marginTop: '10px' }}>
                            If this takes too long, there might be an issue with the Google Maps API key.
                        </p>
                    </>
                )}
            </div>
        );
    }

    return (
        <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
            <div style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                background: 'white',
                padding: '8px',
                borderRadius: '4px',
                display: 'flex',
                gap: '15px',
                marginBottom: '10px',
                marginTop: '10px',
                paddingBottom: '10px',
                paddingTop: '10px',
            }}>
                {contextHolder}
                <button
                    onClick={() => handleMapTypeChange('roadmap')}
                    style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        outline: 'none',
                        color: 'black',
                        backgroundColor: mapType === 'roadmap' ? '#e0e0e0' : 'white'
                    }}
                >
                    Map
                </button>
                <button
                    onClick={() => handleMapTypeChange('satellite')}
                    style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        outline: 'none',
                        color: 'black',
                        backgroundColor: mapType === 'satellite' ? '#e0e0e0' : 'white'
                    }}
                >
                    Satellite
                </button>
                <button
                    onClick={() => handleMapTypeChange('hybrid')}
                    style={{
                        padding: '8px 12px',
                        fontSize: '14px',
                        outline: 'none',
                        color: 'black',
                        backgroundColor: mapType === 'hybrid' ? '#e0e0e0' : 'white'
                    }}
                >
                    Hybrid
                </button>
            </div>
            <GoogleMap
                onLoad={onLoad}
                onUnmount={onUnmount}
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={center}
                mapTypeId={mapType}
                zoom={4.5}
                options={{
                    mapTypeControl: false,
                    fullscreenControl: false,
                    gestureHandling: "greedy",
                    disableDefaultUI: true,
                }}
            >
            </GoogleMap>
            <MapModal visible={visible} onCancel={onCancel} markerData={breakdown} />
        </div>
    );
};

export default Map;